import {
  Fade,
  Grid,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { FormikErrors } from "formik";
import React, { startTransition } from "react";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../components/formsComponents";
import {
  useDispatchWrapper,
} from "../../../../../../../hooks";

import {
  LoadState,
  POLICIES,
  ROLES,
} from "../../../../../../../constants/enums";
import { InsurerCodeAutoSearch } from "../../../../../../../components/AutoCompleteSearches";
import { BrokerCodeField } from "../../../../../../../components/AutoCompleteSearches/BrokerCodeField";
import { ComissionCodeSelect } from "../../../../../../../components/AutoCompleteSearches/ComissionCodeSelect";
import { UsersAutoSearchByRole } from "../../../../../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { useTabsUncontrolledContext } from "../../../../../../../contexts/TabsContextProvider/TabsContextUncontrolledProvider";
import produce from "immer";
import {
  agency_direct_bill,
  payment_plan,
} from "../../../../../../../constants/constants";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import { CustomDatePicker } from "../../../../../../../components/formsComponents/CustomDatePicker";
import { fetchAccountingTaxByPolicy } from "../../../../../../../redux/Accounting/Tax/TaxActions";
import { IPolicyCommission } from "../../../../../../../redux/policyCommission/policyCommission.types";
import { BranchCodeAutoSearch } from "../../../../../../../components/AutoCompleteSearches/BranchCodeAutoSearch";
import { ICustomerPolicyDetails } from "../../../../../../../redux/customerPolicyDetails/customerPolicyDetails.types";
import { PolicyFormSkeleton } from "../../../PoliciesCommon/PolicyFormSkeleton/PolicyFormSkeleton";
import { useRoles } from "../../../../../../../security/RolesProvider/RolesProvider";
import { api } from "../../../../../../../api/api";
import { IInsurerCode } from "../../../../../../../redux/insurer/insurer.types";

interface IMergedFormik {
  values: ICustomerPolicyDetails;
  errors: any;
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T_1 = string | React.ChangeEvent<any>>(
      field: T_1,
    ): T_1 extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void;
  };
  setFieldValue: (field: string, value: any) => void;
  setValues: (
    values: React.SetStateAction<ICustomerPolicyDetails>,
    shouldValidate?: boolean,
  ) => Promise<void> | Promise<FormikErrors<ICustomerPolicyDetails>>;
}

interface IHabitationalPolicyAdminProps {
  customerpolicyId?: number | null;
  // Both props now receive the same merged Formik instance carrying the
  // canonical ICustomerPolicyDetails values (customer info + admin + premium).
  policyAdmin: IMergedFormik;
  policyPremimum: IMergedFormik;
}

export const HabitationalPolicyAdmin: React.FC<
  IHabitationalPolicyAdminProps
> = (props) => {
  const { policyAdmin, policyPremimum } = props;
  // Admin and premium data now arrive through the merged Formik (initialized by
  // the parent via useCustomerPolicyDetails). The loading state is handled by
  // the parent hook, so this subcomponent renders the form directly.
  const loading = LoadState.Loaded as LoadState;
  const premiumDataLoading = LoadState.Loaded as LoadState;
  const dispatch = useDispatchWrapper();
  const { roleName } = useRoles();
  const isProducerDisabled = false;
  const [buttonClicked, setButtonClicked] = React.useState<string | null>(null);
  const { changeStep } = useTabsUncontrolledContext();
  const [expandPremiumSchedule, setExpandPremiumSchedule] =
    React.useState(false);

  const handleCommissionCode = (data: IPolicyCommission | null) => {
    if (data) {
      policyAdmin.setValues({
        ...policyAdmin.values,
        commission_code: data.policy_code,
        commission_percentage: data.commission_new,
      });
    } else {
      policyAdmin.setValues({
        ...policyAdmin.values,
        commission_code: null,
        commission_percentage: null,
      });
    }
  };

  const updatePaymentScheduleTextChange = (
    key: string,
    rowIndex: number,
    value: any,
  ) => {
    const newValues = produce(
      policyPremimum.values.payment_schedule,
      (draftValues) => {
        //@ts-ignore
        draftValues[rowIndex][key] = value;
      },
    );
    policyPremimum.setFieldValue("payment_schedule", newValues);
  };

  const handleTaxRegion = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const billType = policyPremimum.values.agency_bill_or_direct_bill;

    if (billType === "D" && value) {
      startTransition(() => {
        dispatch(
          fetchAccountingTaxByPolicy(
            POLICIES.HABITATIONAL_POLICY,
            value,
            (data) => {
              policyPremimum.setValues((prev) => ({
                ...prev,
                default_tax_region: value,
                tax_one: data ? (data.tax_one as any) : "0",
                tax_two: data ? (data.tax_two as any) : "0",
              }));
            },
          ),
        );
      });
    } else {
      // Set all fields at once to avoid race condition
      policyPremimum.setValues((prev) => ({
        ...prev,
        default_tax_region: value,
        tax_one: "0",
        tax_two: "0",
      }));
    }
  };

  const handleBillTypeChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const currentTaxRegion = policyPremimum.values.default_tax_region;

    if (value === "A" && currentTaxRegion) {
      startTransition(() => {
        dispatch(
          fetchAccountingTaxByPolicy(
            POLICIES.HABITATIONAL_POLICY,
            currentTaxRegion,
            (data) => {
              policyPremimum.setValues((prev) => ({
                ...prev,
                agency_bill_or_direct_bill: value,
                tax_one: data ? (data.tax_one as any) : "0",
                tax_two: data ? (data.tax_two as any) : "0",
              }));
            },
          ),
        );
      });
    } else {
      policyPremimum.setValues((prev) => ({
        ...prev,
        agency_bill_or_direct_bill: value,
        tax_one: "0",
        tax_two: "0",
      }));
    }
  };

  const handlePaymentScheduleTextChange =
    (key: string, rowIndex: number) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updatePaymentScheduleTextChange(key, rowIndex, e.target.value);
    };

  const handlePaymentScheduleDateChange =
    (key: string, rowIndex: number) => (value: any) => {
      updatePaymentScheduleTextChange(key, rowIndex, value);
    };
  // Admin defaults (producer/csr/marketer/branch/default_tax_region) for a new
  // policy are seeded by the owning CustomerInfo Formik initialization effect.
  // Seeding here as well would race with that effect on the same shared Formik
  // and wipe the freshly-seeded values, so it is intentionally not done here.

  const normalizeValue = (backendValue: any) => {
    if (agency_direct_bill[1].value.includes(backendValue)) {
      return "D";
    }
    return backendValue;
  };

  return (
    <>
      <LabelCard heading="Policy Admin" />

      {loading === LoadState.InProgress ? (
        <>
          <PolicyFormSkeleton />
        </>
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={12} lg={4}>
            <CustomFormLabel>Insurer</CustomFormLabel>
            <InsurerCodeAutoSearch
              value={policyAdmin.values.insurer}
              onSelect={async (value) => {
                policyAdmin.setValues({
                  ...policyAdmin.values,
                  insurer: value.value as string,
                  commission_code: null,
                  commission_percentage: null,
                  broker_code: value.agency_code ?? null,
                });
                try {
                  const res = await api.get(
                    `/insurance/get-insurer-code?column=insurer_code&column=name&value=${value.value}`,
                  );
                  const data: IInsurerCode[] = res.data.data;
                  if (data) {
                    policyPremimum.setFieldValue(
                      "agency_bill_or_direct_bill",
                      data[0].default_billing,
                    );
                  }
                } catch (error) {
                  console.error("Error fetching data:", error);
                }
              }}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <CustomFormLabel>Commission Code</CustomFormLabel>
            <ComissionCodeSelect
              insurerCode={policyAdmin.values.insurer}
              value={policyAdmin.values.commission_code}
              onChange={handleCommissionCode}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <CustomFormLabel>Commission %</CustomFormLabel>
            <CustomTextField
              fullWidth
              value={policyAdmin.values.commission_percentage}
              disabled={
                ![
                  ROLES.ADMIN,
                  ROLES.HR,
                  ROLES.ACCOUNTANT,
                  ROLES.MANAGEMENT,
                ].includes(roleName as any)
              }
              onChange={(e) => {
                policyAdmin.setValues({
                  ...policyAdmin.values,
                  commission_percentage: e.target.value,
                });
              }}
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <CustomFormLabel>Branch Code</CustomFormLabel>
            {/* <CustomTextField fullWidth value={values.branch_code} disabled /> */}
            <BranchCodeAutoSearch
              value={policyAdmin.values.branch_code}
              onSelect={(value) => {
                policyAdmin.setFieldValue("branch_code", value.value);
              }}
              errorMessage={policyAdmin.errors.branch_code}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Policy Fee</CustomFormLabel>
            <CustomTextField
              name="policy_fee"
              variant="outlined"
              size="small"
              type="number"
              fullWidth
              value={policyAdmin.values.policy_fee}
              onChange={policyAdmin.handleChange}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              label="Producer 1"
              value={policyAdmin.values.producer_one}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                policyAdmin.setFieldValue("producer_one", data.user_fact_id);
                policyAdmin.setFieldValue("producer_one_code", data.user_code);
              }}
              errorMessage={policyAdmin.errors.producer_one}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              label="Producer 2"
              value={policyAdmin.values.producer_two}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                policyAdmin.setFieldValue("producer_two", data.user_fact_id);
                policyAdmin.setFieldValue("producer_two_code", data.user_code);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              label="Producer 3"
              value={policyAdmin.values.producer_three}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                policyAdmin.setFieldValue("producer_three", data.user_fact_id);
                policyAdmin.setFieldValue(
                  "producer_three_code",
                  data.user_code,
                );
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Previous Producer Code</CustomFormLabel>
            <CustomTextField
              name="previous_producer_code"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyAdmin.values.previous_producer_code}
              disabled
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              value={policyAdmin.values.marketer}
              label="Marketer"
              onSelect={(data) => {
                policyAdmin.setFieldValue("marketer", data.user_fact_id);
                policyAdmin.setFieldValue("marketer_code", data.user_code);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              value={policyAdmin.values.csr}
              label="CSR"
              onSelect={(data) => {
                policyAdmin.setFieldValue("csr", data.user_fact_id);
                policyAdmin.setFieldValue("csr_code", data.user_code);
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Billing Account Number</CustomFormLabel>
            <CustomTextField
              name="billing_account_number"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyAdmin.values.billing_account_number}
              // error={errors.name ? true : false}
              // helperText={errors.name}
              // disabled={isUpdateLead}
              onChange={policyAdmin.handleChange}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Broker Code</CustomFormLabel>
            {/* <BranchCodeAutoSearch
                value={values.broker_code}
                onSelect={(value) => {
                  setFieldValue("broker_code", value.value);
                }}
              /> */}
            <BrokerCodeField
              value={policyAdmin.values.broker_code}
              onChange={(newValue) => policyAdmin.setFieldValue("broker_code", newValue)}
              insurer={policyAdmin.values.insurer}
              branch_code={policyAdmin.values.branch_code}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} lg={2}>
            <CustomFormLabel>Broker Sub Code</CustomFormLabel>
            {/* <BranchCodeAutoSearch
                value={values.broker_code}
                onSelect={(value) => {
                  setFieldValue("broker_code", value.value);
                }}
              /> */}
            <CustomTextField
              name="broker_sub_code"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyAdmin.values.broker_sub_code}
              // error={errors.name ? true : false}
              // helperText={errors.name}
              // disabled={isUpdateLead}
              onChange={policyAdmin.handleChange}
            />
          </Grid>
        </Grid>
      )}

      <LabelCard heading="Premium" />
      <Grid container spacing={1}>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Policy Premium</CustomFormLabel>
          {/* <CurrencyTextField
              fullWidth
              value={policyPremimum.values.premium}
              onChange={(value) => {
                policyPremimum.setFieldValue("premium", value);
              }}
            /> */}
          <CustomTextField
            name="premium"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={policyPremimum.values.premium}
            onChange={policyPremimum.handleChange}
          />
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Agency or Direct Bill</CustomFormLabel>

          <ControlledCustomSelect
            fullWidth
            value={normalizeValue(
              policyPremimum.values.agency_bill_or_direct_bill,
            )}
            name="agency_bill_or_direct_bill"
            onChange={handleBillTypeChange}
            placeholder="Select One"
            displayEmpty
            options={agency_direct_bill.map((option: any) => ({
              label: option.label,
              value: option.label,
            }))}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Payment Plan</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={policyPremimum.values.payment_plan}
            name="payment_plan"
            onChange={policyPremimum.handleChange}
            placeholder="Select one"
            displayEmpty
            options={payment_plan.map((template) => {
              return { label: template, value: template };
            })}
          ></ControlledCustomSelect>
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Premium Finance Contract Number</CustomFormLabel>
          <CustomTextField
            name="premium_finance_contract_number"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={policyPremimum.values.premium_finance_contract_number}
            onChange={policyPremimum.handleChange}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Premium Financed?</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={policyPremimum.values.premium_financed}
            name="premium_financed"
            onChange={policyPremimum.handleChange}
            placeholder="Select one"
            displayEmpty
            options={[
              { label: "Yes", value: "YES" },
              { label: "No", value: "NO" },
            ]}
          ></ControlledCustomSelect>
        </Grid>

        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Financed By</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={policyPremimum.values.financed_by}
            name="financed_by"
            onChange={policyPremimum.handleChange}
            placeholder="Select one"
            displayEmpty
            options={["IFS", "CAFO", "First Insurance Funding", "SNAP"].map(
              (template) => {
                return { label: template, value: template };
              },
            )}
          ></ControlledCustomSelect>
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Defaut Tax Region</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={policyPremimum.values.default_tax_region}
            name="default_tax_region"
            onChange={handleTaxRegion}
            placeholder="Select One"
            displayEmpty
            options={[
              "AB",
              "BC",
              "MB",
              "NB",
              "NL",
              "NS",
              "NT",
              "NU",
              "ON",
              "PE",
              "QC",
              "SK",
              "YT",
            ].map((template) => {
              return { label: template, value: template };
            })}
          ></ControlledCustomSelect>
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Tax 1</CustomFormLabel>
          <CustomTextField
            name="tax_one"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={policyPremimum.values.tax_one}
            // error={errors.name ? true : false}
            // helperText={errors.name}
            // disabled={isUpdateLead}
            onChange={policyPremimum.handleChange}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Tax 2</CustomFormLabel>
          <CustomTextField
            name="tax_two"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={policyPremimum.values.tax_two}
            // error={errors.name ? true : false}
            // helperText={errors.name}
            // disabled={isUpdateLead}
            onChange={policyPremimum.handleChange}
          />
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel> Tax Exempt </CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={policyPremimum.values.tax_exempt}
            name="tax_exempt"
            onChange={policyPremimum.handleChange}
            placeholder="Select one"
            displayEmpty
            options={["Y", "N"].map((template) => {
              return { label: template, value: template };
            })}
          ></ControlledCustomSelect>
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <CustomFormLabel>Tax Exempt Reason</CustomFormLabel>
          <CustomTextField
            name="tax_exempt_reason"
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={policyPremimum.values.tax_exempt_reason}
            // error={errors.name ? true : false}
            // helperText={errors.name}
            // disabled={isUpdateLead}
            onChange={policyPremimum.handleChange}
          />
        </Grid>
      </Grid>

      <LabelCard
        heading="Premium Schedule (Click to Expand)"
        right
        open={expandPremiumSchedule}
        onToggle={() => setExpandPremiumSchedule((prev) => !prev)}
      />

      {premiumDataLoading === LoadState.InProgress ? (
        <PolicyFormSkeleton />
      ) : (
        expandPremiumSchedule && (
          <Fade in={expandPremiumSchedule}>
            <Grid container spacing={1} mt={1}>
              <Grid item xs={12} lg={12}>
                {/* <DataTable {...paymentScheduleTableProps} /> */}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Installment Number</TableCell>
                        <TableCell>Installment Amount</TableCell>
                        <TableCell>Installment Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(policyPremimum.values.payment_schedule || []).map(
                        (item, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  value={item.installment_number}
                                  onChange={handlePaymentScheduleTextChange(
                                    "installment_number",
                                    index,
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  value={item.installment_amount}
                                  onChange={handlePaymentScheduleTextChange(
                                    "installment_amount",
                                    index,
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <CustomDatePicker
                                  fullWidth
                                  value={item.installment_date}
                                  // onChange={updatePaymentScheduleTextChange(
                                  //   "installment_date",
                                  //   index,
                                  // )}
                                  onChange={handlePaymentScheduleDateChange(
                                    "installment_date",
                                    index,
                                  )}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Fade>
        )
      )}
    </>
  );
};
